import { HoverChar } from "@ui/hover-char";
import { cn } from "@u/cn";
import Button from "@ui/button";

type Bp = "sm" | "md" | "lg" | "xl" | "2xl";

const RL_LABEL: Record<Bp, string> = {
  sm: "hidden sm:flex",
  md: "hidden md:flex",
  lg: "hidden lg:flex",
  xl: "hidden xl:flex",
  "2xl": "hidden 2xl:flex",
};
const RL_SQUARE: Record<Bp, string> = {
  sm: "max-sm:aspect-square max-sm:w-auto max-sm:px-0",
  md: "max-md:aspect-square max-md:w-auto max-md:px-0",
  lg: "max-lg:aspect-square max-lg:w-auto max-lg:px-0",
  xl: "max-xl:aspect-square max-xl:w-auto max-xl:px-0",
  "2xl": "max-2xl:aspect-square max-2xl:w-auto max-2xl:px-0",
};

interface HoverCharButtonProps {
  label: string;
  to?: string;
  onClick?: () => void;
  design?: React.ComponentProps<typeof Button>["design"];
  size?: React.ComponentProps<typeof Button>["size"];
  /** Color of the sliding `>` char (e.g. "text-primary"). */
  charClassName?: string;
  className?: string;
  /** Icon shown always; the only thing visible once the label collapses. */
  icon?: React.ComponentType<{ size?: number }>;
  /**
   * Collapse to an icon-only square below this breakpoint (`true` = `sm`).
   * Requires `icon`. Keeps an accessible name via `aria-label`.
   */
  responsiveLabel?: boolean | Bp;
  /** Tooltip text — useful when the label collapses to an icon. */
  floatingLabel?: React.ComponentProps<typeof Button>["floatingLabel"];
  floatingPosition?: React.ComponentProps<typeof Button>["floatingPosition"];
}

/**
 * CTA whose label gains a `>` that slides in on hover — the canonical Andar
 * marketing button idiom (navbar, hero, product CTAs, final CTA). Wraps the
 * shared `Button`, so `design`/`size` and routing (`to`) behave identically.
 */
export function HoverCharButton({
  label,
  to,
  onClick,
  design = "default",
  size = "lg",
  charClassName,
  className,
  icon: Icon,
  responsiveLabel,
  floatingLabel,
  floatingPosition,
}: HoverCharButtonProps) {
  const rlBp: Bp | undefined =
    responsiveLabel === true ? "sm" : responsiveLabel || undefined;
  const collapses = !!rlBp && !!Icon;

  return (
    <Button
      to={to}
      onClick={onClick}
      design={design}
      size={size}
      floatingLabel={floatingLabel}
      floatingPosition={floatingPosition}
      className={cn("group", collapses && RL_SQUARE[rlBp], className)}
      {...(collapses ? { "aria-label": label } : {})}
    >
      <span className="flex items-center justify-center gap-2">
        {Icon && <Icon size={size === "sm" ? 14 : 18} />}
        <span
          className={cn(
            "items-center",
            collapses ? RL_LABEL[rlBp] : "flex",
          )}
        >
          <HoverChar
            className={cn(
              "flex items-center font-mono font-black transition-all group-hover:w-3.5 group-hover:opacity-100",
              charClassName,
            )}
          />
          <span className="tracking-tight">{label}</span>
        </span>
      </span>
    </Button>
  );
}
